import { useState } from 'react';
import { Check, Plus, Star, X } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import type { InvoiceSeries } from '../../../lib/issuedInvoicesApi';
import { useInvoiceSeries, useUpsertInvoiceSeries } from '../../../hooks/useIssuedInvoices';

/**
 * „Serii de facturare” — the series the firm numbers its invoices in. Shows
 * every series with the next number it will hand out, lets a person add a
 * new one (prefix, starting number), make one the default, or move the next
 * number forward. Numbers already allocated are never touched: the series
 * only decides what the next approved invoice is called.
 */
export interface InvoiceSeriesModalProps {
  companyId?: string;
  onClose: () => void;
}

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error && err.message ? err.message : 'A apărut o eroare.';
}

const inputCls =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

export function InvoiceSeriesModal({ companyId, onClose }: InvoiceSeriesModalProps) {
  const series = useInvoiceSeries(companyId);
  const upsert = useUpsertInvoiceSeries();
  const [error, setError] = useState<string | null>(null);

  // New series form.
  const [prefix, setPrefix] = useState('');
  const [startNumber, setStartNumber] = useState('1');
  const [makeDefault, setMakeDefault] = useState(false);

  async function save(input: Parameters<typeof upsert.mutateAsync>[0]) {
    setError(null);
    try {
      await upsert.mutateAsync({ ...input, companyId });
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    }
  }

  async function handleCreate() {
    const p = prefix.trim().toUpperCase();
    const n = Number.parseInt(startNumber, 10);
    if (!p) return setError('Scrie prefixul seriei (ex. CCC).');
    if (!Number.isFinite(n) || n < 1)
      return setError('Numărul de start trebuie să fie cel puțin 1.');
    if (series.data?.some((s) => s.prefix === p)) return setError(`Seria ${p} există deja.`);
    const ok = await save({ prefix: p, nextNumber: n, padding: 4, isDefault: makeDefault });
    if (ok) {
      setPrefix('');
      setStartNumber('1');
      setMakeDefault(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Serii de facturare</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Seria implicită se propune la fiecare factură nouă; o poți schimba pe factură, înainte
              de aprobare. Numărul se alocă la aprobare, din seria aleasă.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-5 px-5 py-4">
          {error && (
            <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}

          {series.isLoading ? (
            <p className="text-sm text-gray-400">Se încarcă…</p>
          ) : (
            <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
              {(series.data ?? []).map((s) => (
                <SeriesRow key={s.id} series={s} busy={upsert.isPending} onSave={save} />
              ))}
            </ul>
          )}

          <div className="rounded-md border border-dashed border-gray-300 p-4">
            <h3 className="text-sm font-semibold text-gray-800">Serie nouă</h3>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Prefix</label>
                <input
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                  placeholder="ex. CCC"
                  maxLength={12}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">Primul număr</label>
                <input
                  type="number"
                  min={1}
                  value={startNumber}
                  onChange={(e) => setStartNumber(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => void handleCreate()}
                  disabled={upsert.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  <Plus size={15} /> Adaugă
                </button>
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              Folosește această serie implicit la facturile noi
            </label>
            <p className="mt-2 text-xs text-gray-500">
              Doar litere, cifre și cratimă. Numărul se completează cu zerouri la 4 cifre (ex. CCC
              0001).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SeriesRow({
  series: s,
  busy,
  onSave,
}: {
  series: InvoiceSeries;
  busy: boolean;
  onSave: (input: {
    prefix: string;
    nextNumber: number;
    padding: number;
    isDefault: boolean;
  }) => Promise<boolean>;
}) {
  const [next, setNext] = useState(String(s.nextNumber));
  const changed = Number.parseInt(next, 10) !== s.nextNumber;

  return (
    <li className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
      <span className="font-mono font-semibold text-gray-900">{s.prefix}</span>
      {s.isDefault ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
          <Star size={11} /> implicită
        </span>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void onSave({
              prefix: s.prefix,
              nextNumber: s.nextNumber,
              padding: s.padding,
              isDefault: true,
            })
          }
          className="text-xs text-brand-700 hover:underline disabled:opacity-50"
        >
          setează implicită
        </button>
      )}
      <span className="ml-auto flex items-center gap-2 text-xs text-gray-600">
        Următorul număr
        <input
          type="number"
          min={1}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
        />
        <span className="font-mono text-gray-500">
          → {s.prefix} {String(Number.parseInt(next, 10) || s.nextNumber).padStart(s.padding, '0')}
        </span>
        {changed && (
          <button
            type="button"
            disabled={busy || !(Number.parseInt(next, 10) >= 1)}
            onClick={() =>
              void onSave({
                prefix: s.prefix,
                nextNumber: Number.parseInt(next, 10),
                padding: s.padding,
                isDefault: s.isDefault,
              })
            }
            className="inline-flex items-center gap-1 rounded bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <Check size={12} /> Salvează
          </button>
        )}
      </span>
    </li>
  );
}
